import { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';

/**
 * Cookie-Consent-Banner fuer Paperball.
 *
 * Zwei Consent-Stufen:
 *   - "essential": Nur strikt notwendige Cookies (Auth-Token, Sprache).
 *     Kein Banner-Klick noetig, immer aktiv.
 *   - "analytics": Erlaubt Multi-Touch-Touchpoint-Recording (POST /api/touchpoints).
 *     Optional, defaults zu OFF.
 *
 * Plausible selbst ist cookieless und braucht KEIN Consent — das Banner
 * regelt nur die persistente Multi-Touch-Journey-Speicherung.
 *
 * Statt-Werte:
 *   localStorage['paperball.consent.decided'] = 'true' / null
 *   localStorage['paperball.consent.analytics'] = 'true' / 'false'
 *
 * Hängt EINMAL an der Wurzel der Anwendung (`AppInhalt`) — bis 28.09.2026 nur
 * im eingeloggten Layout, also nie vor den Besuchern, die über eine Kampagne
 * auf die öffentlichen Seiten kamen. Rendert nichts, wenn schon entschieden
 * ist; „Cookie-Einstellungen“ in beiden Footern öffnet ihn wieder.
 */
/** Öffnet den Banner erneut — für „Cookie-Einstellungen“ in beiden Footern. */
const OEFFNEN = 'paperball:einwilligung-oeffnen';

export function oeffneEinwilligung() {
  window.dispatchEvent(new Event(OEFFNEN));
}

export default function ConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    try {
      const decided = localStorage.getItem('paperball.consent.decided');
      if (!decided) setVisible(true);
    } catch (err) {
      console.error('Einwilligung nicht lesbar — Banner bleibt zu', err);
    }
    // Widerrufen muss so einfach sein wie Einwilligen (Art. 7 Abs. 3 DSGVO):
    // Bis 28.09.2026 gab es nach der ersten Entscheidung keinen Weg zurück.
    const oeffnen = () => {
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener(OEFFNEN, oeffnen);
    return () => window.removeEventListener(OEFFNEN, oeffnen);
  }, []);

  const persist = (analyticsAccepted) => {
    try {
      localStorage.setItem('paperball.consent.decided', 'true');
      localStorage.setItem('paperball.consent.analytics', analyticsAccepted ? 'true' : 'false');
    } catch (err) {
      console.error('Einwilligung liess sich nicht speichern', err);
    }
    if (!analyticsAccepted) {
      // Ohne Einwilligung keine Besucher-Kennung — auch nicht die von vorher.
      import('../lib/attribution')
        .then((mod) => mod.vergissBesucherKennung())
        .catch((err) => console.error('Besucher-Kennung nach Ablehnung nicht entfernt', err));
    }
    if (analyticsAccepted) {
      // Der laufende Besuch zaehlt ab jetzt mit. Die Aufzeichnung lief sonst
      // nur einmal beim Start der Anwendung — also bevor der Banner ueberhaupt
      // beantwortet war. Genau der Besuch, in dem jemand zustimmt, fiel damit
      // als einziger heraus, und das ist der Besuch mit dem Kampagnen-Link.
      import('../lib/attribution')
        .then((mod) => mod.maybeRecordTouchpoint(''))
        .catch((err) => console.error('Touchpoint nach Zustimmung fehlgeschlagen', err));
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label={t('consent.banner.aria', 'Cookie- und Analytics-Einstellungen')}
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        maxWidth: 640,
        margin: '0 auto',
        background: 'var(--bg-primary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-popover)',
        padding: 20,
        zIndex: 9999,
        fontSize: '1rem',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        🍪 {t('consent.banner.title', 'Cookies & Analytics')}
      </div>
      <div style={{ color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.5 }}>
        {t(
          'consent.banner.body',
          'Paperball nutzt nur das Notwendigste — Auth-Cookies und Spracheinstellung. Optional kannst du anonyme Multi-Touch-Analytics erlauben, damit wir verstehen, wie du uns gefunden hast (Plausible Analytics ist immer cookieless aktiv und sammelt keine personenbezogenen Daten).'
        )}
      </div>

      {showDetails && (
        <div style={{
          background: 'var(--bg-tertiary)',
          borderRadius: 'var(--radius)',
          padding: 12,
          marginBottom: 12,
          fontSize: '1rem',
          lineHeight: 1.5,
        }}>
          <div style={{ marginBottom: 6 }}>
            <strong>{t('consent.detail.essential_title', 'Erforderlich (immer aktiv)')}:</strong>{' '}
            {t('consent.detail.essential_body', 'Anmeldung, Sprache, Land, Anzeige-Einstellungen. Dazu die Herkunft deines Besuchs (Kampagne, verweisende Seite, Anzeigen-Klick) und ein Partnercode aus dem Link — sie liegen 30 bzw. 90 Tage in deinem Browser und gehen erst mit deiner Registrierung an uns.')}
          </div>
          <div style={{ marginBottom: 6 }}>
            <strong>{t('consent.detail.plausible_title', 'Plausible (cookieless)')}:</strong>{' '}
            {t('consent.detail.plausible_body', 'Anonyme Seitenaufrufe ohne Cookies, ohne Personenbezug — DSGVO-konform ohne Einwilligung.')}
          </div>
          <div>
            <strong>{t('consent.detail.analytics_title', 'Multi-Touch (optional, opt-in)')}:</strong>{' '}
            {t(
              'consent.detail.analytics_body',
              'Wenn du zustimmst, schicken wir jeden Besuch mit Quelle und Kampagne an unseren Server und ordnen ihn über eine Besucher-Kennung in deinem Browser deinem späteren Konto zu. Kein Tracking auf anderen Websites. Widerrufen kannst du jederzeit unter „Cookie-Einstellungen“ im Footer.'
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          onClick={() => persist(true)}
          data-tooltip={t('consent.button.accept_all_tooltip', 'Erforderlich + Multi-Touch-Analytics erlauben')}
        >
          {t('consent.button.accept_all', 'Alle akzeptieren')}
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => persist(false)}
          data-tooltip={t('consent.button.essential_only_tooltip', 'Nur Erforderliche, kein Multi-Touch-Tracking')}
        >
          {t('consent.button.essential_only', 'Nur Erforderliche')}
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => setShowDetails(!showDetails)}
          style={{ marginLeft: 'auto' }}
          data-tooltip={t('consent.button.details_tooltip', 'Details ein-/ausblenden')}
        >
          {showDetails ? t('consent.button.less', 'Weniger') : t('consent.button.details', 'Details')}
        </button>
      </div>
    </div>
  );
}
